import strftime from "strftime";

/**
 * Context for formatting time
 *
 * The context consists of a start date/time, end date/time, and the current time for evaluating certain conditions.
 */
class FormatContext {
  /**
   * Initialize the context with the start time, end time, and current time
   *
   * @param {Object} options - The context options
   * @param {Date} options.startTime - The start time
   * @param {Date} options.endTime - The end time (optional)
   * @param {Date} options.current - The current time (defaults to now)
   * @param {boolean} options.startTimeIsDateOnly - Flag indicating if startTime is date-only
   * @param {boolean} options.endTimeIsDateOnly - Flag indicating if endTime is date-only
   */
  constructor({
    startTime,
    endTime = null,
    current = new Date(),
    startTimeIsDateOnly = false,
    endTimeIsDateOnly = false,
  }) {
    this.startTime = startTime;
    this.endTime = endTime;
    this.current = current;
    this.startTimeIsDateOnly = startTimeIsDateOnly;
    this.endTimeIsDateOnly = endTimeIsDateOnly;
  }

  /**
   * Check if a start date is present.
   * Should always be true if startTime is provided.
   *
   * @returns {boolean} True if the start date is present, false otherwise
   */
  hasStartDate() {
    return this.startTime !== null;
  }

  /**
   * Check if a start time is present.
   *
   * @returns {boolean} True if the start time is present, false otherwise
   */
  hasStartTime() {
    return this.startTime !== null && !this.startTimeIsDateOnly;
  }

  /**
   * Check if an end date is present.
   * If an end time is present, then the end date component is present if it is on a different date.
   *
   * @returns {boolean} True if the end date is present, false otherwise
   */
  hasEndDate() {
    return this.endTime !== null && this.multiDay();
  }

  /**
   * Check if an end time is present
   *
   * @returns {boolean} True if the end time is present, false otherwise
   */
  hasEndTime() {
    return this.endTime !== null && !this.endTimeIsDateOnly;
  }

  /**
   * Check if the start and end times are on different dates
   *
   * @returns {boolean} True if the start and end times are on different dates, false otherwise
   */
  multiDay() {
    if (!this.startTime || !this.endTime) {
      return false;
    }

    const startDate = new Date(
      this.startTime.getFullYear(),
      this.startTime.getMonth(),
      this.startTime.getDate()
    );
    const endDate = new Date(
      this.endTime.getFullYear(),
      this.endTime.getMonth(),
      this.endTime.getDate()
    );

    return startDate.getTime() !== endDate.getTime();
  }

  /**
   * Check if the start and end dates are both the current year
   * In other words, if the range is contained within the current year
   *
   * @returns {boolean} True if the start and end dates are both the current year, false otherwise
   */
  currentYear() {
    if (!this.startTime) {
      return false;
    }

    const currentYear = this.current.getFullYear();
    const startYear = this.startTime.getFullYear();

    if (startYear !== currentYear) {
      return false;
    }

    if (!this.endTime) {
      return true;
    }

    const endYear = this.endTime.getFullYear();
    return endYear === currentYear;
  }
}

/**
 * Internal class to parse and evaluate the extended format string
 */
class FormatParser {
  /**
   * Initialize the parser with the format string
   *
   * @param {string} format - The format string to parse
   */
  constructor(format) {
    this.format = format;
  }

  /**
   * Evaluate the format string with the provided context
   *
   * @param {FormatContext} context - The context for evaluation
   * @returns {string} The evaluated format string
   */
  evaluate(context) {
    return this.evaluateFormat(this.format, context, context.startTime);
  }

  /**
   * Evaluate the specified format string
   *
   * @param {string} fmt - The format string to evaluate
   * @param {FormatContext} context - The context for evaluation
   * @param {Date} timeBeingFormatted - The time being formatted
   * @returns {string} The evaluated format string
   */
  evaluateFormat(fmt, context, timeBeingFormatted) {
    let result = "";
    let i = 0;

    while (i < fmt.length) {
      if (fmt[i] === "%" && fmt[i + 1] === "[") {
        // Parse block
        const [block, consumed, malformed] = this.parseConditionBlock(
          fmt.substring(i + 2)
        );

        if (malformed) {
          // Append the literal malformed block as-is (including '%[')
          result += `%[${block}`;
        } else {
          result += this.evaluateConditionBlock(
            block,
            context,
            timeBeingFormatted
          );
        }
        i += consumed + 2; // +2 for '%['
      } else if (fmt[i] === "%" && fmt[i + 1] === "Z") {
        // Override %Z for timezone
        //
        // The strftime library does not return short codes with %Z, so we need to
        // extract the timezone from the browser's locale using Intl.DateTimeFormat.
        //
        // The `undefined` locale is used to get the browser's default locale.
        const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
          timeZoneName: "short",
        });
        const parts = dateTimeFormat.formatToParts(timeBeingFormatted);
        const timeZone = parts.find(
          (part) => part.type === "timeZoneName"
        ).value;
        result += timeZone;
        i += 2;
      } else {
        // Handle escapes for %, \, [, ], |
        if (
          fmt[i] === "\\" &&
          i + 1 < fmt.length &&
          ["[", "]", "|", "%", "\\"].includes(fmt[i + 1])
        ) {
          result += fmt[i + 1];
          i += 2;
        } else {
          result += fmt[i];
          i += 1;
        }
      }
    }

    // Pass through to strftime for any remaining codes
    if (timeBeingFormatted instanceof Date) {
      return strftime(result, timeBeingFormatted);
    }

    return result;
  }

  /**
   * Parses a %[...|...|...] condition block, handling escapes and nesting
   *
   * @param {string} str - The string to parse
   * @returns {Array} The parsed block, the number of characters consumed, and whether the block is malformed
   */
  parseConditionBlock(str) {
    let depth = 1;
    let block = "";
    let i = 0;

    while (i < str.length) {
      if (str[i] === "\\" && i + 1 < str.length) {
        block += str[i] + str[i + 1];
        i += 2;
      } else if (str[i] === "[") {
        depth += 1;
        block += str[i];
        i += 1;
      } else if (str[i] === "]" && depth > 1) {
        depth -= 1;
        block += str[i];
        i += 1;
      } else if (str[i] === "]" && depth === 1) {
        // End of block
        return [block, i + 1, false];
      } else {
        block += str[i];
        i += 1;
      }
    }

    // If we get here, block was not closed; treat as literal (malformed)
    return [block, block.length, true];
  }

  /**
   * Evaluates a parsed condition block
   *
   * @param {string} block - The condition block to evaluate
   * @param {FormatContext} context - The context for evaluation
   * @param {Date} timeBeingFormatted - The time being formatted
   * @returns {string} The evaluated condition block
   */
  evaluateConditionBlock(block, context, timeBeingFormatted) {
    // Split on unescaped |
    const parts = this.splitConditionBlockParams(block, "|", 3);
    const condition = parts[0] || "";
    const trueFmt = parts[1] || "";
    const falseFmt = parts[2] || "";

    switch (condition) {
      case "start":
        // If start has a time component
        if (context.hasStartTime()) {
          return this.evaluateFormat(trueFmt, context, context.startTime);
        }

        break;

      case "end":
        // If end has a time component
        if (context.hasEndTime()) {
          return this.evaluateFormat(trueFmt, context, context.endTime);
        }

        break;

      case "start_date":
        // If start date is present (but not necessarily time), format it.
        if (context.hasStartDate()) {
          return this.evaluateFormat(trueFmt, context, context.startTime);
        }

        break;

      case "end_date":
        // If end date is present (but not necessarily time), format it
        if (context.hasEndDate()) {
          return this.evaluateFormat(trueFmt, context, context.endTime);
        }

        break;

      case "current_year":
        // If the (start..end) range is entirely in the current year
        if (context.currentYear()) {
          return this.evaluateFormat(trueFmt, context, timeBeingFormatted);
        }

        break;

      case "not_current_year":
        // If the (start..end) range is not entirely in the current year
        if (!context.currentYear()) {
          return this.evaluateFormat(trueFmt, context, timeBeingFormatted);
        }

        break;

      case "nonzero_minutes":
        // If the time being formatted has a minute component and it is not 0
        if (
          timeBeingFormatted instanceof Date &&
          timeBeingFormatted.getMinutes() !== 0
        ) {
          return this.evaluateFormat(trueFmt, context, timeBeingFormatted);
        }

        break;

      case "multi_day":
        // If the (start..end) range spans multiple days
        if (context.multiDay()) {
          return this.evaluateFormat(trueFmt, context, timeBeingFormatted);
        }

        break;

      default:
        // Unknown condition, treat as literal
        return `%[${block}]`;
    }

    return this.evaluateFormat(falseFmt, context, timeBeingFormatted);
  }

  /**
   * Splits a condition block's parameters on unescaped separator, up to max parts, respecting nested %[ ... ] blocks
   *
   * @param {string} str - The string to split
   * @param {string} sep - The separator to split on
   * @param {number} maxParts - The maximum number of parts to split into
   * @returns {Array<string>} The split parts
   */
  splitConditionBlockParams(str, sep, maxParts) {
    const parts = [];
    let current = "";
    let i = 0;
    let depth = 0;

    while (i < str.length && parts.length < maxParts - 1) {
      if (str[i] === "\\" && i + 1 < str.length) {
        current += str[i] + str[i + 1];
        i += 2;
      } else if (str[i] === "%" && str[i + 1] === "[") {
        depth += 1;
        current += str[i] + str[i + 1];
        i += 2;
      } else if (str[i] === "]" && depth > 0) {
        depth -= 1;
        current += str[i];
        i += 1;
      } else if (str[i] === sep && depth === 0) {
        parts.push(current);
        current = "";
        i += 1;
      } else {
        current += str[i];
        i += 1;
      }
    }

    if (i < str.length) {
      current += str.substring(i);
    }
    parts.push(current);

    // Fill up to maxParts
    while (parts.length < maxParts) {
      parts.push(null);
    }

    return parts;
  }
}

/**
 * Formats a time or time range using an extended strftime-like syntax with conditional blocks.
 *
 * @internal This is an implementation detail of the time formattting. Use the `formatTime` function
 * defined in `index.js` instead.
 *
 * @param {Date} startTime - The start time (required)
 * @param {Date} endTime - The end time (optional)
 * @param {string} format - The format to use, supporting custom conditional blocks
 * @param {Date} current - The reference time for 'current_year' (defaults to now)
 * @param {boolean} startTimeIsDateOnly - Flag indicating if startTime is date-only
 * @param {boolean} endTimeIsDateOnly - Flag indicating if endTime is date-only
 * @returns {string} The formatted string
 */
export function formatTime(startTime, endTime = null, format, options = {}) {
  if (!startTime) {
    throw new Error("startTime is required");
  }

  const {
    current = new Date(),
    startTimeIsDateOnly = false,
    endTimeIsDateOnly = false,
  } = options;

  const context = new FormatContext({
    startTime,
    endTime,
    current,
    startTimeIsDateOnly,
    endTimeIsDateOnly,
  });

  const parser = new FormatParser(format);
  return parser.evaluate(context);
}

export { FormatContext, FormatParser };
